import * as React from "react";

export const useTableSelection = <T extends { id: string }>(
    elements: T[],
    initialSelect?: string[] | "all",
    onSelect?: (selected: string[]) => void
) => {
    const [selectedIds, setSelectedIds] = React.useState<string[]>(
        initialSelect === "all" ? elements.map((element) => element.id) : initialSelect ?? []
    );

    const toggleSelection = (id: string) => {
        const index = selectedIds.indexOf(id);
        let newSelected: string[] = [];
        if (index < 0) {
            newSelected = newSelected.concat(selectedIds, id);
        } else {
            newSelected = newSelected.concat(selectedIds.slice(0, index), selectedIds.slice(index + 1));
        }

        setSelectedIds(newSelected);
        if (onSelect) {
            onSelect(newSelected);
        }
    };

    const toggleSelectAll = () => {
        let selected: string[] = [];
        if (selectedIds.length === elements.length) {
            selected = [];
        } else {
            selected = elements.map((element) => element.id);
        }

        setSelectedIds(selected);
        if (onSelect) {
            onSelect(selected);
        }
    };

    const isSelected = (id: string) => selectedIds.indexOf(id) >= 0;
    const someSelected = selectedIds.length > 0 && selectedIds.length < elements.length;
    const allSelected = selectedIds.length === elements.length && elements.length > 0;
    const clearSelection = React.useCallback(() => setSelectedIds([]), []);

    const getSelectedElements = () => {
        return elements.filter((element) => selectedIds.indexOf(element.id) >= 0);
    };

    return {
        selectedIds,
        getSelectedElements,
        toggleSelection,
        toggleSelectAll,
        isSelected,
        someSelected,
        allSelected,
        clearSelection
    };
};

export type TableSelection = ReturnType<typeof useTableSelection>;
